import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, Star, Settings, Eye, EyeOff,
  ExternalLink, AlertCircle, CheckCircle, User
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CMSGoogleReviewsPage = () => {
  const [config, setConfig] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reviewModal, setReviewModal] = useState({ open: false, review: null });
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/cms/google-reviews');
      setConfig(res.data.config || {});
      setReviews(res.data.reviews || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/cms/google-reviews/config', config);
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncGoogle = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/cms/google-reviews/sync');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveReview = async () => {
    const { review } = reviewModal;
    try {
      if (review.id && !review.id.startsWith('new-')) {
        await api.put(`/cms/google-reviews/${review.id}`, review);
      } else {
        await api.post('/cms/google-reviews', review);
      }
      toast.success('Avis enregistré');
      loadData();
      setReviewModal({ open: false, review: null });
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await api.delete(`/cms/google-reviews/${reviewId}`);
      toast.success('Avis supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleVisibility = async (review) => {
    try {
      await api.put(`/cms/google-reviews/${review.id}`, { is_visible: !review.is_visible });
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openNewReview = () => {
    setReviewModal({
      open: true,
      review: {
        id: `new-${Date.now()}`,
        author_name: '',
        author_photo: '',
        rating: 5,
        text: '',
        date: new Date().toISOString().split('T')[0],
        is_visible: true
      }
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              "w-4 h-4",
              star <= rating ? "fill-amber-400 text-amber-400" : "text-slate-200"
            )}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const visibleReviews = reviews.filter(r => r.is_visible);
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Avis Google</h1>
          <p className="text-slate-500">
            {reviews.length} avis • {visibleReviews.length} visibles • Note moyenne : {avgRating}/5
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfig(!showConfig)}>
            <Settings className="w-4 h-4 mr-2" /> Configuration
          </Button>
          <Button onClick={openNewReview} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un avis
          </Button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <Card className="border-cyan-200 bg-cyan-50/50">
          <CardHeader>
            <CardTitle className="text-lg">Configuration Google Places API</CardTitle>
            <CardDescription>
              Connectez votre compte Google Business pour synchroniser automatiquement les avis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.last_sync ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Synchronisation active</p>
                  <p className="text-sm text-green-700 mt-1">
                    {config.place_name} • {config.average_rating}/5 ({config.total_reviews} avis)<br/>
                    Dernière sync: {new Date(config.last_sync).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Configuration requise</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Entrez votre Place ID Google ci-dessous puis cliquez sur "Synchroniser".
                  </p>
                </div>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Place ID Google</Label>
                <Input
                  value={config?.place_id || ''}
                  onChange={(e) => setConfig({ ...config, place_id: e.target.value })}
                  placeholder="ChIJ... (identifiant de votre établissement)"
                />
                <p className="text-xs text-slate-500">
                  Trouvez votre Place ID sur <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Google Developers</a>
                </p>
              </div>
              <div className="space-y-2">
                <Label>Clé API Google Places</Label>
                <Input
                  type="password"
                  value={config?.api_key || ''}
                  onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                  placeholder="AIza..."
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={config?.auto_sync || false}
                  onCheckedChange={(checked) => setConfig({ ...config, auto_sync: checked })}
                />
                <Label>Synchronisation automatique</Label>
              </div>
              <div className="space-y-2">
                <Label>Note minimum affichée</Label>
                <select
                  className="w-full h-10 border rounded-md px-3"
                  value={config?.min_rating || 4}
                  onChange={(e) => setConfig({ ...config, min_rating: parseInt(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} étoiles et +</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Nombre d'avis affichés</Label>
                <Input
                  type="number"
                  value={config?.display_count || 6}
                  onChange={(e) => setConfig({ ...config, display_count: parseInt(e.target.value) })}
                  min={1}
                  max={20}
                />
              </div>
            </div>
            
            {/* Displayed stats on website */}
            <div className="mt-4 p-4 bg-white border border-slate-200 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Statistiques affichées sur le site</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Note moyenne affichée (ex: 4.9)</Label>
                  <Input
                    value={config?.rating || '4.9'}
                    onChange={(e) => setConfig({ ...config, rating: e.target.value })}
                    placeholder="4.9"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre total d'avis affiché</Label>
                  <Input
                    type="number"
                    value={config?.total_reviews || 150}
                    onChange={(e) => setConfig({ ...config, total_reviews: parseInt(e.target.value) })}
                    min={1}
                    placeholder="150"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Ces valeurs seront affichées dans la section avis Google de la page d'accueil
              </p>
            </div>
            
            <div className="flex justify-between items-center">
              <Button 
                onClick={handleSyncGoogle} 
                disabled={syncing || !config?.place_id}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {syncing ? 'Synchronisation...' : 'Synchroniser depuis Google'}
              </Button>
              <Button onClick={handleSaveConfig} disabled={saving}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer la configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews Grid */}
      {reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Star className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Aucun avis pour le moment</p>
            <p className="text-sm mt-1">Ajoutez des avis manuellement ou configurez la synchronisation Google</p>
            <Button onClick={openNewReview} className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un avis
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map(review => (
            <Card key={review.id} className={cn(!review.is_visible && "opacity-60")}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                      {review.author_photo ? (
                        <img src={review.author_photo} alt={review.author_name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{review.author_name}</p>
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <Badge variant={review.source === 'google_api' ? 'default' : 'secondary'}>
                    {review.source === 'google_api' ? 'Google' : 'Manuel'}
                  </Badge>
                </div>
                
                <p className="mt-4 text-slate-600 text-sm line-clamp-3">
                  {review.text}
                </p>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-xs text-slate-400">
                    {review.date ? new Date(review.date).toLocaleDateString('fr-FR') : ''}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleVisibility(review)}
                      title={review.is_visible ? 'Masquer' : 'Afficher'}
                    >
                      {review.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setReviewModal({ open: true, review })}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {review.source !== 'google_api' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteReview(review.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => !open && setReviewModal({ open: false, review: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewModal.review?.id?.startsWith('new-') ? 'Ajouter un avis' : 'Modifier l\'avis'}
            </DialogTitle>
          </DialogHeader>
          
          {reviewModal.review && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'auteur *</Label>
                  <Input
                    value={reviewModal.review.author_name}
                    onChange={(e) => setReviewModal(prev => ({ ...prev, review: { ...prev.review, author_name: e.target.value } }))}
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={reviewModal.review.date?.split('T')[0] || ''}
                    onChange={(e) => setReviewModal(prev => ({ ...prev, review: { ...prev.review, date: e.target.value } }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Note *</Label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewModal(prev => ({ ...prev, review: { ...prev.review, rating: star } }))}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={cn(
                          "w-8 h-8 transition-colors",
                          star <= reviewModal.review.rating 
                            ? "fill-amber-400 text-amber-400" 
                            : "text-slate-200 hover:text-amber-200"
                        )}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-slate-500">{reviewModal.review.rating}/5</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Avis *</Label>
                <Textarea
                  value={reviewModal.review.text}
                  onChange={(e) => setReviewModal(prev => ({ ...prev, review: { ...prev.review, text: e.target.value } }))}
                  placeholder="Le texte de l'avis..."
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Photo de l'auteur (URL)</Label>
                <Input
                  value={reviewModal.review.author_photo || ''}
                  onChange={(e) => setReviewModal(prev => ({ ...prev, review: { ...prev.review, author_photo: e.target.value } }))}
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={reviewModal.review.is_visible}
                  onCheckedChange={(checked) => setReviewModal(prev => ({ ...prev, review: { ...prev.review, is_visible: checked } }))}
                />
                <Label>Visible sur le site</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, review: null })}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveReview}
              disabled={!reviewModal.review?.author_name || !reviewModal.review?.text}
            >
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSGoogleReviewsPage;
